/**
 * Runtime membership tables for the effort enums.
 *
 * `@ff/domain` declares `EffortSource`, `EffortOutcome` and `DeviceAuthority` as
 * closed unions with no runtime array beside them, and a decoder needs to ask
 * "is this string one of them?" about bytes it did not write. So each table is
 * built from a **total** `Record<Union, true>`: a case added to the union is a
 * compile error here — the record is missing a key — while a hand-written array
 * would silently keep accepting the old set and start quarantining records
 * written by the very build that added the case.
 *
 * Membership is a `Set` rather than the `in` operator because `in` walks the
 * prototype chain, and a decoder that accepted `"toString"` as an effort source
 * would be checking nothing at all.
 *
 * These are membership tables, not a second declaration of the enums. Nothing
 * here names an order, a default, or a label; the union in `@ff/domain` remains
 * the only authority for what the cases are.
 */

import type { DeviceAuthority, EffortOutcome, EffortSource } from "@ff/domain";

function members(table: Readonly<Record<string, true>>): ReadonlySet<string> {
  return new Set(Object.keys(table));
}

const EFFORT_SOURCES: ReadonlySet<string> = members({
  inAppPlan: true,
  inAppDuration: true,
  manual: true,
  healthKit: true,
} satisfies Record<EffortSource, true>);

const EFFORT_OUTCOMES: ReadonlySet<string> = members({
  completed: true,
  partial: true,
  recorded: true,
} satisfies Record<EffortOutcome, true>);

const DEVICE_AUTHORITIES: ReadonlySet<string> = members({
  phone: true,
  mac: true,
  watch: true,
  external: true,
  unknown: true,
} satisfies Record<DeviceAuthority, true>);

export function isEffortSource(value: unknown): value is EffortSource {
  return typeof value === "string" && EFFORT_SOURCES.has(value);
}

export function isEffortOutcome(value: unknown): value is EffortOutcome {
  return typeof value === "string" && EFFORT_OUTCOMES.has(value);
}

export function isDeviceAuthority(value: unknown): value is DeviceAuthority {
  return typeof value === "string" && DEVICE_AUTHORITIES.has(value);
}
